import { useEffect, useRef, useState } from "react";
import { REMINDER_TITLE_MAX, defaultWhen, draftFault, localParts, type Reminder, type ReminderDraft } from "../lib/reminders";
import type { ReminderClient, ReminderWrite } from "../routes/useReminders";

/* `REMIND ME…` — THE ONE REMINDER FORM (W82 step 6). The Dashboard card's `+ Reminder`, the Reminders page's
 * `+ Reminder` and a client's `Remind me…` all open THIS window; editing a row opens it too, with Delete. What it saves
 * goes through `useReminders`' one write path (`draftRow` builds the row), so there is no second shape of a reminder.
 *
 * ⚠️ A NATIVE `<dialog>` OVER A DIMMED PAGE, the house window (`DiscardWindow`, `EquipmentWindows`): Escape closes it
 * without saving, focus lands in the title, the ONE primary is Save.
 * ⚠️ THE CLIENT IS OPTIONAL and pre-filled from where it was opened (a client's page names that client); the when is
 * the coach's own clock (`defaultWhen`: the next whole hour, or 09:00 tomorrow from 21:00).
 * ⚠️ DELETE ASKS ONCE, IN PLACE — a reminder is the coach's own note, so no second window over this one. */
export type ReminderTarget =
  | { mode: "new"; clientId: string | null }
  | { mode: "edit"; reminder: Reminder };

export function ReminderWindow({ target, onClose, clients, write, zone, now }: {
  /** null = closed. */
  readonly target: ReminderTarget | null;
  readonly onClose: () => void;
  readonly clients: readonly ReminderClient[];
  readonly write: ReminderWrite;
  readonly zone: string;
  readonly now: Date;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<ReminderDraft>({ title: "", day: "", time: "", clientId: null });
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const open = target !== null;

  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
    if (target === null) return;
    if (target.mode === "new") {
      const when = defaultWhen(now, zone);
      setDraft({ title: "", day: when.day, time: when.time, clientId: target.clientId });
    } else {
      const when = localParts(target.reminder.dueAt, zone);
      setDraft({ title: target.reminder.title, day: when.day, time: when.time, clientId: target.reminder.clientId });
    }
    setRefusal(null); setBusy(false); setConfirmDelete(false);
  }, [open, target, now, zone]);
  useEffect(() => { if (open) titleRef.current?.focus(); }, [open]);

  async function save() {
    if (target === null) return;
    const fault = draftFault(draft, zone);
    if (fault !== null) { setRefusal(fault); return; }
    setBusy(true); setRefusal(null);
    const failure = target.mode === "new" ? await write.add(draft) : await write.edit(target.reminder.id, draft);
    setBusy(false);
    if (failure !== null) { setRefusal(failure); return; }
    onClose();
  }

  async function remove() {
    if (target?.mode !== "edit") return;
    setBusy(true); setRefusal(null);
    const failure = await write.remove(target.reminder.id);
    setBusy(false);
    if (failure !== null) { setRefusal(failure); return; }
    onClose();
  }

  const pinned = target?.mode === "new" && target.clientId !== null ? clients.find((c) => c.id === target.clientId) : undefined;

  return (
    <dialog ref={dialogRef} className="ht-window ht-confirm reminder-window" aria-labelledby="reminder-window-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="ht-window-frame ht-confirm-frame">
        <h2 className="ht-title ht-confirm-title" id="reminder-window-title">
          {target?.mode === "edit" ? "Edit reminder" : pinned !== undefined ? `Remind me about ${pinned.name}` : "New reminder"}
        </h2>

        <label className="reminder-field">
          <span className="reminder-label">What</span>
          <input ref={titleRef} className="ht-input" value={draft.title} maxLength={REMINDER_TITLE_MAX}
                 placeholder="e.g. Call about the knee"
                 onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void save(); } }} />
        </label>
        <div className="reminder-when">
          <label className="reminder-field">
            <span className="reminder-label">Day</span>
            <input type="date" className="ht-input" value={draft.day} onChange={(e) => setDraft({ ...draft, day: e.target.value })} />
          </label>
          <label className="reminder-field">
            <span className="reminder-label">Time</span>
            <input type="time" className="ht-input" value={draft.time} onChange={(e) => setDraft({ ...draft, time: e.target.value })} />
          </label>
        </div>
        <label className="reminder-field">
          <span className="reminder-label">About</span>
          <select className="ht-input" value={draft.clientId ?? ""}
                  onChange={(e) => setDraft({ ...draft, clientId: e.target.value === "" ? null : e.target.value })}>
            <option value="">No client</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <p className="reminder-hint">Only you see your reminders.</p>

        {refusal !== null && <p className="ht-callout reminder-refusal" role="alert">{refusal}</p>}

        {confirmDelete ? (
          <div className="ht-confirm-actions">
            <span className="reminder-confirm-words">Delete this reminder?</span>
            <button type="button" className="ht-button" onClick={() => setConfirmDelete(false)}>Keep</button>
            <button type="button" className="ht-button ht-button-danger" disabled={busy} onClick={() => { void remove(); }}>Delete</button>
          </div>
        ) : (
          <div className="ht-confirm-actions">
            {target?.mode === "edit" && (
              <button type="button" className="ht-button ht-button-quiet reminder-delete" onClick={() => setConfirmDelete(true)}>Delete</button>
            )}
            <button type="button" className="ht-button" onClick={onClose}>Cancel</button>
            <button type="button" className="ht-button ht-button-primary" disabled={busy} onClick={() => { void save(); }}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>
    </dialog>
  );
}
